// SandboxBrowser component for live preview and AI execution

import React, { useState, useEffect, useRef } from 'react';
import {
  FaRobot,
  FaChevronLeft,
  FaChevronRight,
  FaMagic,
  FaLightbulb,
  FaSyncAlt,
  FaBook,
  FaRocket,
} from 'react-icons/fa';
import { fetchAIPatch } from './aiPatch';

function SandboxBrowser({ url, actions, onClose }) {
              const [currentUrl, setCurrentUrl] = React.useState(url || 'about:blank');
              const [log, setLog] = React.useState([]);
              const [dataInput, setDataInput] = React.useState('');
              const [analysis, setAnalysis] = React.useState('');
              const [goal, setGoal] = React.useState('');
              const [autonomous, setAutonomous] = React.useState(false);
              const [suggestion, setSuggestion] = React.useState('');
              const [reasoningSteps, setReasoningSteps] = React.useState([]);
              const [rankedOptions, setRankedOptions] = React.useState([]);

              // Advanced autonomous agent loop with multi-step reasoning and ranking
              // Now includes slide/presentation generation and export
              const [slides, setSlides] = React.useState([]);
              const [slideHtml, setSlideHtml] = React.useState('');
              React.useEffect(() => {
                let running = true;
                async function runAgent() {
                  if (!autonomous || !goal) return;
                  setLog(prev => [...prev, `ðŸ¤– AI Agent: Goal received: "${goal}"`]);
                  setReasoningSteps([]);
                  setRankedOptions([]);
                  setSlides([]);
                  setSlideHtml('');
                  // Step 1: Understand the goal
                  setTimeout(() => {
                    if (!running) return;
                    setReasoningSteps(rs => [...rs, 'Step 1: Understand the user goal and constraints.']);
                    setLog(prev => [...prev, 'ðŸ¤– AI Agent: Researching relevant websites...']);
                    // Step 2: Simulate web research
                    setTimeout(() => {
                      if (!running) return;
                      setReasoningSteps(rs => [...rs, 'Step 2: Gather and extract data from multiple sources.']);
                      setLog(prev => [...prev, 'ðŸ¤– AI Agent: Extracting and analyzing data...']);
                      // Step 3: Analyze and score options or generate slides
                      setTimeout(() => {
                        if (!running) return;
                        setReasoningSteps(rs => [...rs, 'Step 3: Score and rank all options or generate slides.']);
                        // Detect if the goal is about slides/presentation
                        if (/slide|presentation|powerpoint|pptx/i.test(goal)) {
                          // Simulate AI slide generation (Markdown array)
                          const generatedSlides = [
                            { title: 'Welcome', content: 'This is an AI-generated presentation.' },
                            { title: 'Your Topic', content: goal },
                            { title: 'Key Points', content: '- Point 1\n- Point 2\n- Point 3' },
                            { title: 'Conclusion', content: 'Thank you!' }
                          ];
                          setSlides(generatedSlides);
                          // Convert to HTML for preview
                          const html = `
                            <div style='font-family:sans-serif;background:#fff;padding:24px;'>
                              ${generatedSlides.map(slide => `
                                <div style='border:2px solid #4ec9b0;border-radius:12px;margin-bottom:24px;padding:24px;'>
                                  <h2 style='color:#4ec9b0;'>${slide.title}</h2>
                                  <div>${slide.content.replace(/\n/g,'<br/>')}</div>
                                </div>
                              `).join('')}
                            </div>
                          `;
                          setSlideHtml(html);
                          setSuggestion('Presentation generated. Preview below.');
                          setAnalysis('ðŸ¤– AI Agent: Presentation ready. Export downloads slides as Markdown.');
                          setLog(prev => [...prev, 'ðŸ¤– AI Agent: Presentation generated.']);
                        } else {
                          // ...existing code for other goals...
                          let options = [];
                          if (/safe.*area|where.*live|budget|city|neighbo?rhood/i.test(goal)) {
                            options = [
                              { name: 'Midtown', safety: 9, cost: 7, schools: 8 },
                              { name: 'Riverside', safety: 10, cost: 8, schools: 7 },
                              { name: 'Oakwood', safety: 8, cost: 6, schools: 9 },
                              { name: 'Downtown', safety: 6, cost: 5, schools: 6 }
                            ];
                            options.forEach(o => { o.score = o.safety * 2 + o.cost + o.schools; });
                            options.sort((a, b) => b.score - a.score);
                            setRankedOptions(options);
                            setSuggestion('Best areas based on your budget and safety:\n' + options.slice(0, 3).map(o => `- ${o.name} (score: ${o.score})`).join('\n'));
                          } else if (/financial|investment|stock|portfolio/i.test(goal)) {
                            options = [
                              { name: 'AAPL', growth: 9, risk: 4 },
                              { name: 'MSFT', growth: 8, risk: 3 },
                              { name: 'TSLA', growth: 10, risk: 8 },
                              { name: 'BND', growth: 4, risk: 1 }
                            ];
                            options.forEach(o => { o.score = o.growth - o.risk; });
                            options.sort((a, b) => b.score - a.score);
                            setRankedOptions(options);
                            setSuggestion('Top investment options:\n' + options.slice(0, 3).map(o => `- ${o.name} (score: ${o.score})`).join('\n'));
                          } else {
                            options = [
                              { name: 'Option A', relevance: 8, cost: 7 },
                              { name: 'Option B', relevance: 9, cost: 6 },
                              { name: 'Option C', relevance: 7, cost: 8 }
                            ];
                            options.forEach(o => { o.score = o.relevance * 2 - o.cost; });
                            options.sort((a, b) => b.score - a.score);
                            setRankedOptions(options);
                            setSuggestion('Best options for your goal:\n' + options.slice(0, 3).map(o => `- ${o.name} (score: ${o.score})`).join('\n'));
                          }
                          setAnalysis('ðŸ¤– AI Agent: Analysis complete.\n- Multi-step reasoning\n- Advanced scoring/ranking\n- Ready for next action');
                          setLog(prev => [...prev, 'ðŸ¤– AI Agent: Suggestion ready.']);
                        }
                      }, 1400);
                    }, 1400);
                  }, 1400);
                }
                runAgent();
                return () => { running = false; };
              }, [autonomous, goal]);

              React.useEffect(() => {
                if (actions && actions.length > 0) {
                  actions.forEach((a, i) => {
                    setTimeout(() => setLog(prev => [...prev, `AI: ${a.type} - ${a.description || ''}`]), 600 * i);
                  });
                }
              }, [actions]);

              // Simulate AI data analysis (including financial data)
              const handleAnalyze = () => {
                if (!dataInput.trim()) return setAnalysis('Please paste data to analyze.');
                if (/\d{4}-\d{2}-\d{2}/.test(dataInput) && /\d+\.\d+/.test(dataInput)) {
                  setAnalysis('Detected financial data.\nSummary: Revenue and expenses parsed.\nAI: Trends, outliers, and totals calculated. (Demo)');
                } else if (dataInput.includes(',') || dataInput.includes('\t')) {
                  setAnalysis('Detected tabular data.\nAI: Columns and rows parsed. (Demo)');
                } else {
                  setAnalysis('AI: Data received. (Demo)');
                }
              };

              // Slide export (Markdown â€” avoids optional pptxgenjs bundling)
              function exportSlidesToPPTX() {
                const md = slides.map((s) => `# ${s.title}\n${s.content}`).join('\n\n');
                const blob = new Blob([md], { type: 'text/markdown' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = 'AI_Presentation.md';
                a.click();
              }


              const [musicNotes, setMusicNotes] = React.useState([]);
              const [musicAudioUrl, setMusicAudioUrl] = React.useState("");

              function generateMelody(length = 8) {
                const scale = [261.63, 293.66, 329.63, 349.23, 392.0, 440.0, 493.88, 523.25];
                const notes = [];
                for (let i = 0; i < length; i++) {
                  notes.push(scale[Math.floor(Math.random() * scale.length)]);
                }
                setMusicNotes(notes);
                return notes;
              }

              async function playMelody(notes, tempo = 120) {
                if (!notes || !notes.length) return;
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const duration = 60 / tempo;
                const now = ctx.currentTime;
                notes.forEach((freq, i) => {
                  const osc = ctx.createOscillator();
                  const gain = ctx.createGain();
                  osc.type = "sine";
                  osc.frequency.value = freq;
                  gain.gain.value = 0.18;
                  osc.connect(gain).connect(ctx.destination);
                  osc.start(now + i * duration);
                  osc.stop(now + (i + 1) * duration - 0.02);
                });
              }

              React.useEffect(() => {
                if (!autonomous || !goal) return;
                if (/music|melody|song|compose|audio/i.test(goal)) {
                  setLog((prev) => [...prev, "ðŸŽµ AI Agent: Generating melody..."]);
                  generateMelody(12);
                  setSuggestion("A simple melody has been generated. Click Play to listen.");
                  setAnalysis("ðŸŽµ AI Agent: Music composition complete.");
                }
              }, [autonomous, goal]);

              const [rocketDesign, setRocketDesign] = React.useState(null);
              React.useEffect(() => {
                if (!autonomous || !goal) return;
                if (/rocket|spacecraft|launch|orbital|propulsion/i.test(goal)) {
                  setLog((prev) => [...prev, "ðŸš€ AI Agent: Designing a rocket..."]);
                  const design = {
                    stages: Math.floor(Math.random() * 2) + 2,
                    height: (30 + Math.random() * 40).toFixed(1),
                    diameter: (2 + Math.random() * 3).toFixed(1),
                    payload: (2 + Math.random() * 18).toFixed(1),
                    thrust: (400 + Math.random() * 800).toFixed(0),
                    fuel: ["RP-1/LOX", "Methalox", "Solid", "Hydrolox"][Math.floor(Math.random() * 4)],
                  };
                  setRocketDesign(design);
                  setSuggestion("A basic rocket design has been generated. See summary and SVG below.");
                  setAnalysis("ðŸš€ AI Agent: Rocket design complete.");
                }
              }, [autonomous, goal]);

              const [tradingSim, setTradingSim] = React.useState(null);
              React.useEffect(() => {
                if (!autonomous || !goal) return;
                if (/trading|stock|portfolio|buy|sell|market|trade|exchange/i.test(goal)) {
                  setLog((prev) => [...prev, "ðŸ’¹ AI Agent: Starting live trading simulation..."]);
                  const stocks = ["AAPL", "GOOG", "TSLA", "MSFT", "AMZN"];
                  const prices = Object.fromEntries(stocks.map((s) => [s, (80 + Math.random() * 120).toFixed(2)]));
                  setTradingSim({ balance: 10000, portfolio: {}, prices, history: [] });
                  setSuggestion("A live trading simulation is running. Buy/sell stocks below.");
                  setAnalysis("ðŸ’¹ AI Agent: Trading simulation ready.");
                }
              }, [autonomous, goal]);

              function buyStock(symbol) {
                setTradingSim((sim) => {
                  if (!sim) return sim;
                  const price = parseFloat(sim.prices[symbol]);
                  if (sim.balance < price) return sim;
                  const portfolio = { ...sim.portfolio };
                  portfolio[symbol] = (portfolio[symbol] || 0) + 1;
                  return {
                    ...sim,
                    balance: sim.balance - price,
                    portfolio,
                    history: [...sim.history, `Bought 1 ${symbol} @ $${price.toFixed(2)}`],
                  };
                });
              }
              function sellStock(symbol) {
                setTradingSim((sim) => {
                  if (!sim) return sim;
                  if (!sim.portfolio[symbol]) return sim;
                  const price = parseFloat(sim.prices[symbol]);
                  const portfolio = { ...sim.portfolio };
                  portfolio[symbol] -= 1;
                  if (portfolio[symbol] <= 0) delete portfolio[symbol];
                  return {
                    ...sim,
                    balance: sim.balance + price,
                    portfolio,
                    history: [...sim.history, `Sold 1 ${symbol} @ $${price.toFixed(2)}`],
                  };
                });
              }
              return (
                <div className="sandbox-browser-modal" style={{ position: 'fixed', left: 0, right: 0, bottom: 80, margin: '0 auto', width: 600, zIndex: 10001, background: '#181a20', border: '2px solid #4ec9b0', borderRadius: 12, boxShadow: '0 8px 32px #0008', padding: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#23272e', borderTopLeftRadius: 10, borderTopRightRadius: 10, padding: '8px 16px', borderBottom: '1px solid #4ec9b0' }}>
                    <span style={{ color: '#4ec9b0', fontWeight: 700, fontSize: 15, flex: 1 }}>Sandbox Browser (Autonomous AI Agent)</span>
                    <button onClick={onClose} style={{ background: 'none', color: '#fff', border: 'none', fontSize: 18, cursor: 'pointer', marginLeft: 8 }}>Ã—</button>
                  </div>
                  {/* Slide Preview */}
                  {slides.length > 0 && slideHtml && (
                    <div style={{ background: '#fff', borderBottomLeftRadius: 10, borderBottomRightRadius: 10, padding: 0, minHeight: 180 }}>
                      <div dangerouslySetInnerHTML={{ __html: slideHtml }} />
                      <button onClick={exportSlidesToPPTX} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer', margin: 12 }}>Export slides (.md)</button>
                    </div>
                  )}
                  {/* Fallback: Website Preview */}
                  {(!slides.length || !slideHtml) && (
                    <div style={{ padding: 0, background: '#fff', borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}>
                      <iframe src={currentUrl} title="Sandbox Preview" style={{ width: '100%', height: 180, border: 'none', borderBottomLeftRadius: 10, borderBottomRightRadius: 10, background: '#fff' }} sandbox="allow-scripts allow-same-origin" />
                    </div>
                  )}
                  <div style={{ background: '#23272e', color: '#4ec9b0', fontSize: 13, padding: '8px 16px', borderBottom: '1px solid #4ec9b0' }}>
                    <div style={{ marginBottom: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={goal}
                        onChange={e => setGoal(e.target.value)}
                        placeholder="Enter AI goal (e.g. Create a presentation about AI)"
                        style={{ flex: 1, borderRadius: 6, border: '1px solid #4ec9b0', background: '#181a20', color: '#fff', fontSize: 13, padding: '6px 10px' }}
                      />
                      <button onClick={() => setAutonomous(a => !a)} style={{ background: autonomous ? '#4ec9b0' : '#23272e', color: autonomous ? '#23272e' : '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>{autonomous ? 'Stop' : 'Start'} AI</button>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <textarea
                        value={dataInput}
                        onChange={e => setDataInput(e.target.value)}
                        placeholder="Paste data here (CSV, financial, etc.)"
                        style={{ width: '100%', minHeight: 48, borderRadius: 6, border: '1px solid #4ec9b0', background: '#181a20', color: '#fff', fontSize: 13, marginBottom: 6 }}
                      />
                      <button onClick={handleAnalyze} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>Analyze Data</button>
                    </div>
                    {reasoningSteps.length > 0 && (
                      <div style={{ color: '#a5b4fc', background: '#23272e', borderRadius: 6, padding: 8, marginBottom: 8, whiteSpace: 'pre-line', fontWeight: 500 }}>
                        <strong>Reasoning Steps:</strong>\n{reasoningSteps.join('\n')}
                      </div>
                    )}
                    {rankedOptions.length > 0 && (
                      <div style={{ color: '#fbbf24', background: '#23272e', borderRadius: 6, padding: 8, marginBottom: 8, whiteSpace: 'pre-line', fontWeight: 500 }}>
                        <strong>Ranked Options:</strong>\n{rankedOptions.map(o => `- ${o.name} (score: ${o.score})`).join('\n')}
                      </div>
                    )}
                    {suggestion && <div style={{ color: '#fff', background: '#23272e', borderRadius: 6, padding: 8, marginBottom: 8, whiteSpace: 'pre-line', fontWeight: 600 }}>Suggestion: {suggestion}</div>}
                    {analysis && <div style={{ color: '#fff', background: '#23272e', borderRadius: 6, padding: 8, marginBottom: 8, whiteSpace: 'pre-line' }}>{analysis}</div>}
                    {log.length > 0 ? log.map((l, i) => <div key={i}>{l}</div>) : <span>AI actions will appear hereâ€¦</span>}
                  </div>
                  {/* Add music composition UI if goal includes 'music' or 'compose' */}
                  {musicNotes.length > 0 && (
                    <div style={{ background: '#181a20', color: '#fff', fontSize: 13, padding: '8px 16px', borderBottom: '1px solid #4ec9b0', marginTop: 12 }}>
                      <div style={{ fontWeight: 700, color: '#4ec9b0', marginBottom: 4 }}>Music Composition</div>
                      <button onClick={() => playMelody(musicNotes)} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '6px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer', marginBottom: 8 }}>Play Melody</button>
                      <div style={{ fontSize: 12, marginTop: 4 }}>Notes (Hz): {musicNotes.map(n => n.toFixed(2)).join(", ")}</div>
                    </div>
                  )}
                  {/* Add rocket design UI below the agent log */}
                  {rocketDesign && (
                    <div style={{ background: '#181a20', color: '#fff', fontSize: 13, padding: '8px 16px', borderBottom: '1px solid #4ec9b0', marginTop: 12 }}>
                      <div style={{ fontWeight: 700, color: '#4ec9b0', marginBottom: 4 }}>Rocket Design</div>
                      <div style={{ fontSize: 12, marginBottom: 6 }}>
                        <strong>Stages:</strong> {rocketDesign.stages} &nbsp; <strong>Height:</strong> {rocketDesign.height} m &nbsp; <strong>Diameter:</strong> {rocketDesign.diameter} m<br/>
                        <strong>Payload:</strong> {rocketDesign.payload} tons &nbsp; <strong>Thrust:</strong> {rocketDesign.thrust} kN &nbsp; <strong>Fuel:</strong> {rocketDesign.fuel}
                      </div>
                      {/* Simple SVG rocket visualization */}
                      <svg width="60" height="180" viewBox="0 0 60 180" style={{ background: '#23272e', borderRadius: 8, margin: '8px 0' }}>
                        {/* Body */}
                        <rect x="20" y="30" width="20" height="100" fill="#4ec9b0" />
                        {/* Nose cone */}
                        <polygon points="30,10 15,30 45,30" fill="#fbbf24" />
                        {/* Fins */}
                        <polygon points="20,130 10,150 20,150" fill="#e57373" />
                        <polygon points="40,130 50,150 40,150" fill="#e57373" />
                        {/* Flame */}
                        <polygon points="30,150 20,170 40,170" fill="#f59e42" />
                      </svg>
                    </div>
                  )}
                  {/* Add live trading UI below the agent log */}
                  {tradingSim && (
                    <div style={{ background: '#181a20', color: '#fff', fontSize: 13, padding: '8px 16px', borderBottom: '1px solid #4ec9b0', marginTop: 12 }}>
                      <div style={{ fontWeight: 700, color: '#4ec9b0', marginBottom: 4 }}>Live Trading Simulation</div>
                      <div style={{ fontSize: 12, marginBottom: 6 }}>
                        <strong>Balance:</strong> ${tradingSim.balance.toFixed(2)}<br/>
                        <strong>Portfolio:</strong> {Object.keys(tradingSim.portfolio).length === 0 ? 'None' : Object.entries(tradingSim.portfolio).map(([s, n]) => `${s}: ${n}`).join(', ')}
                      </div>
                      <table style={{ width: '100%', background: '#23272e', borderRadius: 8, margin: '8px 0', fontSize: 12 }}>
                        <thead>
                          <tr style={{ color: '#4ec9b0' }}>
                            <th style={{ textAlign: 'left', padding: 4 }}>Stock</th>
                            <th style={{ textAlign: 'right', padding: 4 }}>Price</th>
                            <th style={{ textAlign: 'center', padding: 4 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(tradingSim.prices).map(([symbol, price]) => (
                            <tr key={symbol}>
                              <td style={{ padding: 4 }}>{symbol}</td>
                              <td style={{ padding: 4, textAlign: 'right' }}>${price}</td>
                              <td style={{ padding: 4, textAlign: 'center' }}>
                                <button onClick={() => buyStock(symbol)} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, marginRight: 4, cursor: 'pointer' }}>Buy</button>
                                <button onClick={() => sellStock(symbol)} style={{ background: '#e57373', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontWeight: 600, cursor: 'pointer' }}>Sell</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 11, marginTop: 8 }}>
                        <strong>History:</strong>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {tradingSim.history.length === 0 ? <li>No trades yet.</li> : tradingSim.history.map((h, i) => <li key={i}>{h}</li>)}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
}

// AI-driven code actions modal (scaffold)
function CodeActionsModal({ onClose }) {
  return (
    <div className="code-actions-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(24,26,32,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#23272e', borderRadius: 10, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 32px #0008', textAlign: 'center' }}>
        <h2 style={{ color: '#fff', marginBottom: 16 }}>AI-Driven Code Actions</h2>
        <div style={{ color: '#aaa', marginBottom: 18 }}>
          (Right-click context menu actions like "Explain with AI", "Refactor with AI", "Fix with AI" coming soon. This is a placeholder for AI code actions.)
        </div>
        <button onClick={onClose} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}
// Git integration modal (scaffold)
function GitIntegrationModal({ onClose, filePath }) {
  const [commits, setCommits] = React.useState([]);
  const [blame, setBlame] = React.useState([]);
  React.useEffect(() => {
    if (!filePath) return;
    fetch(`/api/git/commits?file=${encodeURIComponent(filePath)}`)
      .then((res) => res.json())
      .then((data) => setCommits(data.commits || []))
      .catch(() => setCommits([]));
    fetch(`/api/git/blame?file=${encodeURIComponent(filePath)}`)
      .then(res => res.json())
      .then(data => setBlame(data.blame || []))
      .catch(() => setBlame([]));
  }, [filePath]);
  return (
    <div className="git-integration-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(24,26,32,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#23272e', borderRadius: 10, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 32px #0008', textAlign: 'center' }}>
        <h2 style={{ color: '#fff', marginBottom: 16 }}>Git Integration</h2>
        <div style={{ color: '#aaa', marginBottom: 18 }}>
          <strong>Commit History</strong>
          <ul style={{ textAlign: 'left', margin: '10px 0 18px 0', padding: 0, listStyle: 'none' }}>
            {commits.length === 0 && <li style={{ color: '#666' }}>No commits found.</li>}
            {commits.map((c, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <span style={{ color: '#4ec9b0' }}>{c.hash?.slice(0,7)}</span> {c.message} <span style={{ color: '#888' }}>({c.author}, {c.date})</span>
              </li>
            ))}
          </ul>
          <strong>Inline Blame</strong>
          <ul style={{ textAlign: 'left', margin: '10px 0 18px 0', padding: 0, listStyle: 'none' }}>
            {blame.length === 0 && <li style={{ color: '#666' }}>No blame info found.</li>}
            {blame.map((b, i) => (
              <li key={i} style={{ marginBottom: 6 }}>
                <span style={{ color: '#4ec9b0' }}>{b.author}</span> on line {b.line}: <span style={{ color: '#aaa' }}>{b.summary}</span>
              </li>
            ))}
          </ul>
        </div>
        <button onClick={onClose} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}
// Advanced theming modal (scaffold)
function ThemeSettingsModal({ onClose }) {
  // Live theme preview logic
  const handleThemeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const theme = JSON.parse(evt.target.result);
        if (theme && typeof theme === 'object') {
          Object.entries(theme).forEach(([key, value]) => {
            document.documentElement.style.setProperty(`--${key}`, value);
          });
        }
      } catch (_) {}
    };
    reader.readAsText(file);
  };
  return (
    <div className="theme-settings-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(24,26,32,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#23272e', borderRadius: 10, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 32px #0008', textAlign: 'center' }}>
        <h2 style={{ color: '#fff', marginBottom: 16 }}>Advanced Theming</h2>
        <div style={{ color: '#aaa', marginBottom: 18 }}>
          Upload a JSON file with CSS variable mappings (e.g. {'{"primary-color":"#4ec9b0"}'}) to preview your theme live.
        </div>
        <input type="file" accept="application/json" onChange={handleThemeUpload} style={{ marginBottom: 18, color: '#fff' }} />
        <button onClick={onClose} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}
// Voice command palette modal (scaffold)
function VoiceCommandModal({ onClose, onResult }) {
  const [listening, setListening] = React.useState(false);
  const [error, setError] = React.useState('');
  const recognitionRef = React.useRef(null);

  const startListening = () => {
    setError('');
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Speech recognition not supported in this browser.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      if (onResult) onResult(transcript);
      setListening(false);
      onClose();
    };
    recognition.onerror = (event) => {
      setError('Error: ' + event.error);
      setListening(false);
    };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };
  const stopListening = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setListening(false);
  };
  return (
    <div className="voice-command-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(24,26,32,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#23272e', borderRadius: 10, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 32px #0008', textAlign: 'center' }}>
        <h2 style={{ color: '#fff', marginBottom: 16 }}>Voice Command Palette</h2>
        <div style={{ color: '#aaa', marginBottom: 18 }}>
          {listening ? 'Listening... Speak now.' : 'Click below to start voice input.'}
        </div>
        {error && <div style={{ color: '#ff6a6a', marginBottom: 12 }}>{error}</div>}
        <button
          onClick={listening ? stopListening : startListening}
          style={{ background: listening ? '#ff6a6a' : '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer', marginBottom: 12 }}
        >
          {listening ? 'Stop' : 'Start Voice Input'}
        </button>
        <br />
        <button onClick={onClose} style={{ background: '#23272e', color: '#fff', border: '1px solid #4ec9b0', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer', marginTop: 8 }}>Close</button>
      </div>
    </div>
  );
}
// Settings modal for customizable shortcuts (scaffold)
function ShortcutsSettingsModal({ shortcuts, onChange, onClose }) {
  return (
    <div className="shortcuts-settings-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(24,26,32,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#23272e', borderRadius: 10, padding: 24, minWidth: 420, maxWidth: 600, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 4px 32px #0008' }}>
        <h2 style={{ color: '#fff', marginBottom: 16 }}>Customize Shortcuts</h2>
        <table style={{ width: '100%', color: '#fff', fontSize: 14, marginBottom: 18 }}>
          <thead>
            <tr style={{ color: '#4ec9b0', fontWeight: 600 }}>
              <td>Action</td>
              <td>Shortcut</td>
            </tr>
          </thead>
          <tbody>
            {Object.entries(shortcuts).map(([action, key], idx) => (
              <tr key={action}>
                <td style={{ padding: '6px 8px' }}>{action}</td>
                <td style={{ padding: '6px 8px' }}>
                  <input
                    type="text"
                    value={key}
                    onChange={e => onChange(action, e.target.value)}
                    style={{ width: 90, padding: 4, borderRadius: 4, border: '1px solid #444', background: '#181a20', color: '#fff' }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={onClose} style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Close</button>
      </div>
    </div>
  );
}

function MultiFilePatchModal({ patches, onApply, onClose }) {
  return (
    <div
      className="multi-file-patch-modal"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(24,26,32,0.92)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          background: '#23272e',
          borderRadius: 10,
          padding: 24,
          minWidth: 420,
          maxWidth: 640,
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 4px 32px #0008',
        }}
      >
        <h2 style={{ color: '#fff', marginBottom: 16 }}>Multi-file patch review</h2>
        {!patches || patches.length === 0 ? (
          <div style={{ color: '#888' }}>No patches.</div>
        ) : (
          <ul style={{ color: '#ccc', textAlign: 'left', fontSize: 13, listStyle: 'none', padding: 0, margin: 0 }}>
            {patches.map((p, i) => (
              <li key={i} style={{ marginBottom: 12 }}>
                <strong style={{ color: '#4ec9b0' }}>{p.file}</strong>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11, margin: '6px 0 0 0' }}>{p.diff}</pre>
              </li>
            ))}
          </ul>
        )}
        <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#444',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onApply}
            style={{
              background: '#4ec9b0',
              color: '#23272e',
              border: 'none',
              borderRadius: 6,
              padding: '8px 16px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Apply all
          </button>
        </div>
      </div>
    </div>
  );
}

function AIAssistant({ tabbedEditorRef }) {
  const [model, setModel] = useState('gpt-5.2');
  const [collapsed, setCollapsed] = useState(false);
  const [animatedMsgId, setAnimatedMsgId] = useState(null);
  const [animatedContent, setAnimatedContent] = useState('');
  // New: Animate patch application line by line
  const [patchAnimating, setPatchAnimating] = useState(false);
  const [patchLines, setPatchLines] = useState([]);
  const [patchCurrent, setPatchCurrent] = useState(0);
  const [patchMsg, setPatchMsg] = useState(null);

  // Handler to request and animate patch
  const handlePatchRequest = async ({ fileContent, language, prompt, onApply }) => {
    setPatchAnimating(true);
    setPatchLines([]);
    setPatchCurrent(0);
    setPatchMsg(null);
    // 1. Get Manus AI reasoning/plan (simulate or call API)
    const manusPlan = await fetchAIPatch({ code: fileContent, language, prompt, model: 'manus' });
    // 2. Get Cursor AI patch (simulate or call API)
    const cursorPatch = await fetchAIPatch({ code: fileContent, language, prompt, model: 'cursor' });
    // 3. Merge or sequence both (for demo, show Manus plan, then apply Cursor patch)
    if (manusPlan && Array.isArray(manusPlan) && manusPlan.length > 0) {
      // Show Manus reasoning/plan in chat bubble
      setPatchMsg('Manus AI reasoning:');
      for (let i = 0; i < manusPlan.length; ++i) {
        setPatchLines(manusPlan.slice(0, i + 1));
        await new Promise(res => setTimeout(res, 350));
      }
      setPatchMsg('Applying Cursor AI patch...');
      setPatchLines([]);
    }
    if (cursorPatch && Array.isArray(cursorPatch)) {
      let idx = 0;
      const interval = setInterval(() => {
        setPatchLines(cursorPatch.slice(0, idx + 1));
        setPatchCurrent(idx + 1);
        if (idx < cursorPatch.length) {
          if (onApply) onApply(cursorPatch[idx]);
        }
        idx++;
        if (idx >= cursorPatch.length) {
          clearInterval(interval);
          setPatchAnimating(false);
          setPatchMsg('AI patch applied!');
        }
      }, 350);
    } else {
      setPatchAnimating(false);
    }
  };
  const [animatedLines, setAnimatedLines] = useState([]);
  const [isAnimatingLines, setIsAnimatingLines] = useState(false);
  const [models, setModels] = useState([
    { label: 'GPT-5.2', value: 'gpt-5.2' },
    { label: 'Gemini', value: 'gemini' },
    { label: 'Grok', value: 'grok' },
    { label: 'Composer', value: 'composer' },
    { label: 'Sonet', value: 'sonet' },
    { label: 'Opus', value: 'opus' },
    { label: 'Claude', value: 'claude' },
    { label: 'Haiku', value: 'haiku' }
  ]);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'ðŸ‘‹ Welcome to Zolaid AI! I can help you with:\n\nâœ¨ Code generation\nðŸ’¡ Code explanation\nðŸ”„ Refactoring\nðŸ› Bug fixing\nðŸ“š Documentation\nâš¡ Performance optimization',
      timestamp: new Date()
    }
  ]);
  // Chat history search
  const [searchTerm, setSearchTerm] = useState('');
  const filteredMessages = searchTerm.trim()
    ? messages.filter(m => (m.content || '').toLowerCase().includes(searchTerm.toLowerCase()))
    : messages;

  // Auto-update to latest models from backend if available
  useEffect(() => {
    fetch('/api/models/latest-models')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data.models) && data.models.length > 0) {
          setModels(data.models.map(m => ({ label: m.name, value: m.id })));
          if (!data.models.some(m => m.id === model)) {
            setModel(data.models[0].id);
          }
        }
      })
      .catch(() => {});
  }, []);
  
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  const [reasoning, setReasoning] = useState(null);

  // AI-driven code actions state (scaffold)
  const [showCodeActionsModal, setShowCodeActionsModal] = useState(false);
  const [codeActionSelection, setCodeActionSelection] = useState('');
  const [showGitModal, setShowGitModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const defaultShortcuts = {
    'Send Message': 'Ctrl+Enter',
    'Multi-File Patch': 'Ctrl+M',
    'Start Live Share': 'Ctrl+L',
    'Search Chat': 'Ctrl+F',
    'Open Settings': 'Ctrl+,'
  };
  const [shortcuts, setShortcuts] = useState(() => {
    try {
      const saved = localStorage.getItem('zolaid.shortcuts');
      return saved ? JSON.parse(saved) : defaultShortcuts;
    } catch (_) {
      return defaultShortcuts;
    }
  });
  const handleShortcutChange = (action, value) => {
    setShortcuts(prev => {
      const updated = { ...prev, [action]: value };
      try {
        localStorage.setItem('zolaid.shortcuts', JSON.stringify(updated));
      } catch (_) {}
      return updated;
    });
  };
  const [multiFilePatches, setMultiFilePatches] = useState([]);
  const [showPatchModal, setShowPatchModal] = useState(false);

  // Real-time collaboration state (scaffold)
  const [collabActive, setCollabActive] = useState(false);
  const [collabSessionId, setCollabSessionId] = useState('');
  const [collabStatus, setCollabStatus] = useState('');

  // Generate a random session code
  function randomSessionCode() {
    return Math.random().toString(36).substr(2, 8).toUpperCase();
  }

  // Handlers for starting/joining a session (localStorage demo)
  const handleStartCollab = () => {
    const code = randomSessionCode();
    setCollabActive(true);
    setCollabSessionId(code);
    setCollabStatus('Session started. Share this code to invite others.');
    try { localStorage.setItem('zolaid.collabSession', code); } catch (_) {}
  };
  const handleJoinCollab = () => {
    const code = prompt('Enter session code:');
    if (code) {
      setCollabActive(true);
      setCollabSessionId(code);
      setCollabStatus('Joined collaboration session.');
      try { localStorage.setItem('zolaid.collabSession', code); } catch (_) {}
    }
  };
  const handleLeaveCollab = () => {
    setCollabActive(false);
    setCollabSessionId('');
    setCollabStatus('');
    try { localStorage.removeItem('zolaid.collabSession'); } catch (_) {}
  };
  // On mount, restore session if present
  React.useEffect(() => {
    try {
      const code = localStorage.getItem('zolaid.collabSession');
      if (code) {
        setCollabActive(true);
        setCollabSessionId(code);
        setCollabStatus('Restored collaboration session.');
      }
    } catch (_) {}
  }, []);

  // Handler to trigger multi-file patch review (fully functional)
  const handleMultiFilePatch = async (prompt) => {
    if (!tabbedEditorRef || !tabbedEditorRef.current) return;
    const openFiles = tabbedEditorRef.current.tabs?.map(t => ({ path: t.path, content: t.content })) || [];
    const res = await fetch('/api/ai/multi-file-patch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files: openFiles, prompt })
    });
    const data = await res.json();
    setMultiFilePatches(data.patches || []);
    setShowPatchModal(true);
  };

  const handleApplyAllPatches = () => {
    if (!tabbedEditorRef || !tabbedEditorRef.current) return;
    // Apply each patch to the corresponding file in the editor
    multiFilePatches.forEach(patch => {
      if (tabbedEditorRef.current.applyAIPatchToFile) {
        tabbedEditorRef.current.applyAIPatchToFile(patch.file, patch.diff);
      }
    });
    setShowPatchModal(false);
    setMultiFilePatches([]);
    // Optionally show notification/confirmation
  };

  // Animate AI response typing
  useEffect(() => {
    if (animatedMsgId !== null) {
      const aiMsg = messages.find(m => m.id === animatedMsgId);
      if (!aiMsg) return;
      // If the message is mostly code, animate line by line
      const codeBlockMatch = aiMsg.content.match(/```[\s\S]*?```/g);
      if (codeBlockMatch && codeBlockMatch.length > 0) {
        // Animate code block line by line
        const code = codeBlockMatch[0].replace(/```[a-z]*\n?|```/g, '');
        const lines = code.split('\n');
        setAnimatedLines([]);
        setIsAnimatingLines(true);
        let idx = 0;
        const interval = setInterval(() => {
          idx++;
          setAnimatedLines(lines.slice(0, idx));
          if (idx >= lines.length) {
            clearInterval(interval);
            setIsAnimatingLines(false);
            setAnimatedMsgId(null);
          }
        }, 180);
        return () => clearInterval(interval);
      } else {
        // Fallback: animate as normal typing
        let i = 0;
        setAnimatedContent('');
        const interval = setInterval(() => {
          i++;
          setAnimatedContent(aiMsg.content.slice(0, i));
          if (i >= aiMsg.content.length) {
            clearInterval(interval);
            setAnimatedMsgId(null);
          }
        }, 12);
        return () => clearInterval(interval);
      }
    }
  }, [animatedMsgId, messages]);

  const simulateAIResponse = async (userMessage) => {
    setIsProcessing(true);

    // Add user message
    const userMsg = {
      id: messages.length + 1,
      type: 'user',
      content: userMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      // Call backend API with selected model
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: 'ide-demo',
          message: userMessage,
          model,
          source: 'ide'
        })
      });
      const data = await res.json();
      let aiContent = data?.contract?.content || data?.reply || 'No response.';
      // Optionally show model name
      aiContent += `\n\n_Model: ${models.find(m => m.value === model)?.label || model}_`;
      const aiMsg = {
        id: messages.length + 2,
        type: 'ai',
        content: aiContent,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMsg]);
      setAnimatedMsgId(aiMsg.id);
    } catch (e) {
      setMessages(prev => [...prev, {
        id: messages.length + 2,
        type: 'ai',
        content: 'Error contacting backend: ' + e.message,
        timestamp: new Date()
      }]);
    }
    setIsProcessing(false);
  };

  const handleSendMessage = async (message = null) => {
    const messageToSend = message || input.trim();
    if (!messageToSend || isProcessing) return;

    simulateAIResponse(messageToSend);
    setInput('');

    // Auto-trigger AI patch if code-related and file is open
    const codeKeywords = ['fix', 'refactor', 'optimize', 'bug', 'generate', 'update', 'implement', 'code', 'function', 'class'];
    const isCodeRequest = codeKeywords.some(k => messageToSend.toLowerCase().includes(k));
    if (isCodeRequest && tabbedEditorRef && tabbedEditorRef.current && tabbedEditorRef.current.applyAIPatch) {
      // Get active file content and language
      const tabs = tabbedEditorRef.current;
      const activeTab = tabs.tabs?.find(t => t.path === tabs.activeTab);
      if (activeTab) {
        const fileContent = activeTab.content;
        const language = activeTab.path.endsWith('.js') ? 'javascript' : activeTab.path.endsWith('.py') ? 'python' : 'plaintext';
        // Fetch patch and animate/apply
        handlePatchRequest({
          fileContent,
          language,
          prompt: messageToSend,
          onApply: (patchLine) => tabbedEditorRef.current.applyAIPatch([patchLine])
        });
      }
    }
  };

  const handleQuickAction = (action) => {
    const prompts = {
      generate: 'Generate a function to fetch user data',
      explain: 'Explain this code',
      refactor: 'Refactor this code',
      fix: 'Fix the bugs in this code',
      document: 'Generate documentation for this code',
      optimize: 'Optimize this code for performance'
    };
    
    handleSendMessage(prompts[action]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSendMessage();
    }
  };

  return (
    <div className={`ai-assistant-container${collapsed ? ' collapsed' : ''}`}> 
      {/* AI-driven code actions modal */}
      {showCodeActionsModal && (
        <CodeActionsModal onClose={() => setShowCodeActionsModal(false)} />
      )}
      {/* Git integration modal */}
      {showGitModal && (
        <GitIntegrationModal
          onClose={() => setShowGitModal(false)}
          filePath={tabbedEditorRef && tabbedEditorRef.current && tabbedEditorRef.current.activeTab}
        />
      )}
      {/* Advanced theming modal */}
      {showThemeModal && (
        <ThemeSettingsModal onClose={() => setShowThemeModal(false)} />
      )}
      {/* Voice command palette modal */}
      {showVoiceModal && (
        <VoiceCommandModal
          onClose={() => setShowVoiceModal(false)}
          onResult={txt => setInput(txt)}
        />
      )}
      {/* Shortcuts settings modal */}
      {showShortcutsModal && (
        <ShortcutsSettingsModal
          shortcuts={shortcuts}
          onChange={handleShortcutChange}
          onClose={() => setShowShortcutsModal(false)}
        />
      )}
      {/* Multi-file patch modal */}
      {showPatchModal && (
        <MultiFilePatchModal
          patches={multiFilePatches}
          onApply={handleApplyAllPatches}
          onClose={() => setShowPatchModal(false)}
        />
      )}
      {/* Real-time collaboration controls */}
      <div style={{ background: '#181a20', borderBottom: '1px solid #23272e', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10 }}>
        {!collabActive ? (
          <>
            <button
              style={{ background: '#4ec9b0', color: '#23272e', border: 'none', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              onClick={handleStartCollab}
            >
              Start Live Share
            </button>
            <button
              style={{ background: 'none', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              onClick={handleJoinCollab}
            >
              Join Session
            </button>
          </>
        ) : (
          <>
            <span style={{ color: '#4ec9b0', fontWeight: 600 }}>
              {collabSessionId ? `Session Code: ${collabSessionId}` : 'In Collaboration Session'}
            </span>
            <button
              style={{ background: 'none', color: '#fff', border: '1px solid #444', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              onClick={handleLeaveCollab}
            >
              Leave
            </button>
          </>
        )}
        {collabStatus && <span style={{ color: '#aaa', marginLeft: 10 }}>{collabStatus}</span>}
      </div>
      <div className="ai-header floating-header">
        <div className="ai-header-avatar">
          <FaRobot size={28} style={{ color: '#4ec9b0', marginRight: 8 }} />
        </div>
        <div className="ai-header-title">
          <span className="status-indicator"></span>
          <h1>Zolaid AI Assistant</h1>
        </div>
        <div className="ai-model-picker">
          <label htmlFor="ai-model-select" className="model-label">Model:</label>
          <select
            id="ai-model-select"
            value={model}
            onChange={e => setModel(e.target.value)}
            className="model-select"
          >
            {models.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        <button
          className="collapse-btn"
          title={collapsed ? 'Expand AI Assistant' : 'Collapse AI Assistant'}
          onClick={() => setCollapsed(v => !v)}
        >
          {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
        </button>
      </div>


      {!collapsed && <>
        {/* Chat history search bar */}
        <div style={{ padding: '8px 12px 0 12px', background: '#181a20', borderBottom: '1px solid #23272e' }}>
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search chat history..."
            style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #23272e', background: '#23272e', color: '#fff', fontSize: 13 }}
          />
        </div>
        <div className="ai-messages enhanced-messages-bg">
          {filteredMessages.length === 0 && (
            <div style={{ color: '#888', textAlign: 'center', margin: '16px 0' }}>No messages found.</div>
          )}
          {filteredMessages.map(msg => (
            <div key={msg.id} className={`message message-${msg.type} enhanced-bubble`}>
              <div className="message-content">
                {msg.type === 'ai' ? (
                  msg.id === animatedMsgId && isAnimatingLines ? (
                    <div className="ai-content ai-typing">
                      <pre style={{margin:0,background:'none',padding:0}}>
                        {animatedLines.map((line, i) => (
                          <div key={i} style={{fontFamily:'monospace',fontSize:13}}>{line}</div>
                        ))}
                        <span className="ai-cursor">|</span>
                      </pre>
                    </div>
                  ) : msg.id === animatedMsgId ? (
                    <div className="ai-content ai-typing">{animatedContent}<span className="ai-cursor">|</span></div>
                  ) : (
                    <div className="ai-content" dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }} />
                  )
                ) : (
                  <div className="user-content">{msg.content}</div>
                )}
              </div>
              <div className="message-time">
                {msg.timestamp.toLocaleTimeString()}
              </div>
            </div>
          ))}
          {/* Patch animation bubble */}
          {patchAnimating && (
            <div className="message message-ai enhanced-bubble">
              <div className="message-content">
                <div className="ai-content ai-typing">
                  <pre style={{margin:0,background:'none',padding:0}}>
                    {patchLines.map((patch, i) => (
                      <div key={i} style={{fontFamily:'monospace',fontSize:13}}>
                        {patch.line !== undefined ? `Line ${patch.line + 1}: ` : ''}{patch.newLine}
                      </div>
                    ))}
                    <span className="ai-cursor">|</span>
                  </pre>
                </div>
              </div>
              <div className="message-time">AI patching...</div>
            </div>
          )}
          {patchMsg && (
            <div className="message message-ai enhanced-bubble">
              <div className="message-content">
                <div className="ai-content">{patchMsg}</div>
              </div>
            </div>
          )}
          {isProcessing && !reasoning && (
            <div className="processing-indicator">
              <div className="spinner"></div>
              Processing...
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Reasoning/thinking indicator above typing box */}
        {reasoning && (
          <div className="reasoning-indicator" style={{ margin: '0 0 8px 0', borderRadius: 8, background: '#23272e', border: '1px solid #3e3e42', padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="reasoning-icon">{reasoning.icon}</span>
            <span className="reasoning-text">{reasoning.text}</span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
        )}

        <div className="ai-input-area">
          {/* AI-driven code actions context menu integration (Monaco editor) */}
          {tabbedEditorRef && tabbedEditorRef.current && tabbedEditorRef.current.monaco && !showCodeActionsModal && (
            (() => {
              const monaco = tabbedEditorRef.current.monaco;
              if (!monaco._zolaidCodeActionListener) {
                monaco._zolaidCodeActionListener = monaco.editor.onContextMenu(e => {
                  const selection = e.target && e.target.position ? monaco.editor.getModels()[0].getLineContent(e.target.position.lineNumber) : '';
                  setCodeActionSelection(selection);
                  setShowCodeActionsModal(true);
                });
              }
            })()
          )}
          {/* AI-driven code actions button */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => setShowCodeActionsModal(true)}
          >
            AI Code Actions
          </button>
          {/* Git integration button */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => setShowGitModal(true)}
          >
            Git Integration
          </button>
          {/* Advanced theming button */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => setShowThemeModal(true)}
          >
            Advanced Theming
          </button>
          {/* Voice command palette button */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => setShowVoiceModal(true)}
          >
            Voice Command Palette
          </button>
          {/* Settings button to open shortcuts modal */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => setShowShortcutsModal(true)}
          >
            Settings / Shortcuts
          </button>
          {/* Multi-file patch trigger button (for demo/testing) */}
          <button
            className="quick-action-btn"
            style={{ marginBottom: 8, background: '#23272e', color: '#4ec9b0', border: '1px solid #4ec9b0', borderRadius: 6, padding: '6px 14px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
            onClick={() => handleMultiFilePatch(input || 'Suggest improvements across files')}
            disabled={isProcessing}
          >
            Multi-File AI Patch
          </button>
          <div className="quick-actions enhanced-quick-actions">
            <button
              className="quick-action-btn"
              title="Generate code"
              onClick={() => handleQuickAction('generate')}
              disabled={isProcessing}
            >
              <FaMagic style={{ marginRight: 4 }} /> Generate
            </button>
            <button
              className="quick-action-btn"
              title="Explain code"
              onClick={() => handleQuickAction('explain')}
              disabled={isProcessing}
            >
              <FaLightbulb style={{ marginRight: 4 }} /> Explain
            </button>
            <button
              className="quick-action-btn"
              title="Refactor code"
              onClick={() => handleQuickAction('refactor')}
              disabled={isProcessing}
            >
              <FaSyncAlt style={{ marginRight: 4 }} /> Refactor
            </button>
            <button
              className="quick-action-btn"
              title="Document code"
              onClick={() => handleQuickAction('document')}
              disabled={isProcessing}
            >
              <FaBook style={{ marginRight: 4 }} /> Document
            </button>
            <button
              className="quick-action-btn"
              title="Optimize code"
              onClick={() => handleQuickAction('optimize')}
              disabled={isProcessing}
            >
              <FaRocket style={{ marginRight: 4 }} /> Optimize
            </button>
          </div>

          <div className="input-group">
            <textarea
              className="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything... (Ctrl+Enter to send)"
              disabled={isProcessing}
              rows="1"
            />
            <button
              className="send-btn"
              onClick={() => handleSendMessage()}
              disabled={isProcessing || !input.trim()}
            >
              Send
            </button>
          </div>
        </div>
      </>}
    </div>
  );
}

function formatMarkdown(text) {
  let html = text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br/>')
    .replace(/```javascript\n([\s\S]*?)\n```/g, '<pre><code>$1</code></pre>')
    .replace(/- (.*?)\n/g, '<li>$1</li>')
    .replace(/(<li>.*?<\/li>)/s, '<ul>$1</ul>');
  
  return `<p>${html}</p>`;
}

export default AIAssistant;
